// @ts-nocheck
import cloneDeep from 'lodash-es/cloneDeep'
import set from 'lodash-es/set'
import unescape from 'lodash-es/unescape'

import type { Offer } from '@fourthwall/services/dist/models/offer'
import type { VariantType } from '@fourthwall/services/dist/models/variantType'
import { formatCurrency } from '@fourthwall/utils/dist/currency'

import { getEstimatedPaymentProcessingFees } from './ProductPricing/components/CalculateProfit/utils'
import { convertNumberToMoney } from './ProductPricing/utils/convertNumberToMoney'
import { convertNumberToPrice } from './ProductPricing/utils/convertNumberToPrice'
import type { GetInitialValuesProps, PricingType, ProductFormState } from './SingleProduct.types'

export interface Dictionary<T> {
  [index: string]: T
}

export const getInitialValues = ({ offer }: GetInitialValuesProps): ProductFormState => {
  const {
    name,
    description,
    categoryId,
    variants,
    visualHints,
    variantTypes,
    images,
    memberships: { hasAllowedTiers, allowedTiersType, tiersIds },
    digitalItems,
    fulfillmentService,
    manufacturingService,
    shipment,
    bespokeProductId,
    customsInformation,
    metafields,
    requirePhoneNumberFromBuyer,
  } = offer

  const isCompareAtPriceEnabled = variants.some((variant) => !!variant.compareAtPrice)

  const isCreatorDeclaredCostEnabled =
    manufacturingService === 'MANUFACTURED_BY_CREATOR' &&
    typeof metafields === 'object' &&
    metafields !== null &&
    'declareCreatorCost' in metafields &&
    Boolean((metafields as Record<string, unknown>).declareCreatorCost)

  return {
    name: unescape(name),
    description: unescape(description),
    categoryId,
    sellingPriceValue: variants[0]?.price ? formatCurrency(variants[0]?.price.value) : '',
    isCompareAtPriceEnabled,
    compareAtPriceValue:
      isCompareAtPriceEnabled && variants[0]?.compareAtPrice
        ? formatCurrency(variants[0]?.compareAtPrice?.value)
        : null,
    isCreatorDeclaredCostEnabled,
    visualHints,
    isSoldOut: !offer.state?.available,
    offerImages: images ? images.map((image) => ({ ...image, tags: image.tags.tags })) : [],
    withMembersOnly: hasAllowedTiers,
    membersOnlyType: allowedTiersType,
    membersOnlyTierIds: tiersIds,
    variantTypes,
    shipment: {
      type:
        shipment?.shipmentStartDate.type === 'UNSET' ||
        shipment?.shipmentStartDate.type === 'BUSINESS_DAYS_RANGE'
          ? 'DAYS_RANGE'
          : shipment?.shipmentStartDate.type || 'UNKNOWN',
      days: {
        daysMin:
          shipment?.shipmentStartDate.type === 'DAYS_RANGE'
            ? shipment?.shipmentStartDate.daysMin.toString()
            : undefined,
        daysMax:
          shipment?.shipmentStartDate.type === 'DAYS_RANGE'
            ? shipment?.shipmentStartDate.daysMax.toString()
            : undefined,
      },
      dates: {
        from:
          shipment?.shipmentStartDate.type === 'DATE_RANGE'
            ? new Date(shipment?.shipmentStartDate.from)
            : undefined,
        to:
          shipment?.shipmentStartDate.type === 'DATE_RANGE'
            ? new Date(shipment?.shipmentStartDate.to)
            : undefined,
      },
    },
    shipmentDateUpdatePendingOrders: true,
    shipmentDateNotifyPendingSupporters: true,
    shipmentDateUpdateReason: '',
    variants: variants.map((variant) => {
      let result = cloneDeep(variant)
      if (variant.price) {
        result = set(
          result,
          'price',
          convertNumberToPrice(result.price.value, result.price.currency),
        )
      }
      if (variant.compareAtPrice) {
        result = set(
          result,
          'compareAtPrice',
          convertNumberToPrice(result.compareAtPrice!.value, result.compareAtPrice!.currency),
        )
      }

      if (variant.creatorDeclaredCost) {
        result = set(
          result,
          'creatorDeclaredCost',
          convertNumberToPrice(
            result.creatorDeclaredCost.value,
            result.creatorDeclaredCost.currency,
          ),
        )
      }

      if (variant.weight) {
        result = set(result, 'weight.value', result.weight.value.toString())
      }

      return result
    }),
    digitalItems: digitalItems.map((digitalItem) => ({
      id: digitalItem.uri,
      url: digitalItem.uri,
      name: digitalItem.name,
      size: digitalItem.metadata?.fileSize,
    })),
    fulfillmentService,
    manufacturingService,
    bespokeProductId,
    isCustomsInformationEnabled:
      fulfillmentService !== 'FULFILLED_BY_CREATOR' ||
      manufacturingService !== 'MANUFACTURED_BY_CREATOR' ||
      Boolean(customsInformation?.shortHsCode) ||
      Boolean(customsInformation?.countryOfOrigin),
    customsInformation: {
      ...customsInformation,
      hasHsCodes: true,
    },
    shippingPackageSize: offer.config?.shippingConfig.shippingPackageSize,
    requirePhoneNumberFromBuyer,
  }
}

export const getHasDifferentSizes = (sizeVariant: VariantType | undefined): boolean => {
  return !!sizeVariant && sizeVariant.options.length > 1
}

export const getInitialPricingType = ({
  doProfitsVary,
  hasDifferentSizes,
  sameSizesHaveDifferentCosts,
  doCompareAtPricesVary,
}: {
  doProfitsVary: boolean
  hasDifferentSizes: boolean
  sameSizesHaveDifferentCosts: boolean
  doCompareAtPricesVary: boolean
}): PricingType => {
  return (doProfitsVary && hasDifferentSizes && !sameSizesHaveDifferentCosts) ||
    doCompareAtPricesVary
    ? 'EXPLICIT'
    : 'IMPLICIT'
}

export const convertCompareAtPriceToMoneyValueOrNull = (
  compareAtPrice: Offer.Variant['compareAtPrice'] | null,
): Offer.Variant['compareAtPrice'] | null =>
  compareAtPrice?.value ? convertNumberToMoney(compareAtPrice.value) : null

export const convertCreatorDeclaredCostToMoneyValueOrNull = (
  creatorDeclaredCost: Offer.Variant['creatorDeclaredCost'] | null,
): Offer.Variant['creatorDeclaredCost'] | null =>
  creatorDeclaredCost?.value ? convertNumberToMoney(creatorDeclaredCost.value) : null

export const convertSizeToBytes = (sizeString?: string) => {
  if (sizeString === undefined) return 0

  const parts = sizeString.split(' ')
  const number = parseFloat(parts[0])
  const unit = parts[1]

  if (!unit) return number

  switch (unit.toUpperCase()) {
    case 'KB':
      return number * 1024
    case 'MB':
      return number * 1024 * 1024
    case 'GB':
      return number * 1024 * 1024 * 1024
    default:
      return number
  }
}

export const getTotalDigitalFilesSize = (
  digitalItems: ProductFormState['digitalItems'] | Offer.Item['digitalItems'],
) => {
  return digitalItems.reduce((acc, file) => acc + convertSizeToBytes(file.size), 0)
}

export const isFreeProduct = (offer: ProductFormState | Offer.Item) =>
  offer.variants.some((variant) => Number(variant.price.value) === 0)

export const getMinRequiredPrice = (manufacturingPrice: number) => {
  const priceWithInitialFees =
    manufacturingPrice + getEstimatedPaymentProcessingFees(manufacturingPrice)
  const priceWithCompoundedFees =
    manufacturingPrice + getEstimatedPaymentProcessingFees(priceWithInitialFees)

  return Number(priceWithCompoundedFees.toFixed(2))
}

export const findFirstVariantRequiringPaymentMethod = (values: ProductFormState) => {
  const variant = values.variants.find((variant) => {
    const minRequiredPrice = getMinRequiredPrice(Number(variant.cost?.value || 0))

    return variant.price.value < minRequiredPrice
  })

  if (!variant) return null

  const manufacturingPrice = Number(variant.cost?.value || 0)
  const minRequiredPrice = {
    value: getMinRequiredPrice(manufacturingPrice),
    currency: variant.price.currency,
  }
  const profit = variant.price.value - manufacturingPrice

  return {
    reason: profit < 0 ? 'loss' : 'belowMinimumProfit',
    price: variant.price,
    correctPrice: minRequiredPrice,
  }
}
